import React, { FC } from 'react';
import RadioGroupGrid from 'ts/common/components/RadioGroupGrid';
import { useTranslate } from 'ts/common/hooks';
import { convertIsoToMinutes, formatIsoWallClockTime } from '../helpers';

interface ITimePickerProps {
    timeslots: string[];
    selectedTimeslot?: string;
    onTimeslotChange: (timeslot: string) => void;
}

interface TimeOption {
    name: string;
    value: number;
}

const TimePicker: FC<ITimePickerProps> = ({ selectedTimeslot, timeslots, onTimeslotChange }) => {
    const t = useTranslate('booking.dateAndTimePage');

    const splitTimeslots = (slots: string[]) => {
        const AMOptions: TimeOption[] = [];
        const PMOptions: TimeOption[] = [];
        const MilitaryOptions: TimeOption[] = [];

        slots.forEach((slot) => {
            // Prefer ISO wall-clock labels so times stay correct when browser tzdata is stale.
            const name = formatIsoWallClockTime(slot);
            const value = convertIsoToMinutes(slot);

            if (name.toLowerCase().includes('am')) {
                AMOptions.push({ name, value });
            } else if (name.toLowerCase().includes('pm')) {
                PMOptions.push({ name, value });
            } else {
                MilitaryOptions.push({ name, value });
            }
        });

        return { AMOptions, PMOptions, MilitaryOptions };
    };

    const { AMOptions, PMOptions, MilitaryOptions } = splitTimeslots(timeslots);

    /*
        This function calls the callback function onTimeslotChange with the
        selectedSlot selection which at this time should be a number
        from the parent component. We take that number and do a compare to each
        of the string timeslots that are converted to minutes. Upon a match
        we return that string timeslot.
    */
    const handleSelection = (value: number) => {
        const selectedSlot =
            timeslots && timeslots.find((slot) => convertIsoToMinutes(slot) === value);

        // We do not have a default selection, so we want to protect against this value being null
        if (selectedSlot) {
            onTimeslotChange(selectedSlot);
        }
    };

    const selectedTimeValue = selectedTimeslot ? convertIsoToMinutes(selectedTimeslot) : undefined;

    return (
        <>
            {AMOptions.length > 0 && (
                <RadioGroupGrid
                    columnOverride={3}
                    isClientBooking
                    label="AM"
                    fieldName="AMTimes"
                    onChange={(_, value) => handleSelection(value)}
                    options={AMOptions}
                    parentFormValue={selectedTimeValue}
                />
            )}

            {PMOptions.length > 0 && (
                <RadioGroupGrid
                    columnOverride={3}
                    isClientBooking
                    label="PM"
                    fieldName="PMTimes"
                    onChange={(_, value) => handleSelection(value)}
                    options={PMOptions}
                    parentFormValue={selectedTimeValue}
                />
            )}

            {MilitaryOptions.length > 0 && (
                <RadioGroupGrid
                    columnOverride={3}
                    isClientBooking
                    label={t('timeSelect')}
                    fieldName="MilitaryTimes"
                    onChange={(_, value) => handleSelection(value)}
                    options={MilitaryOptions}
                    parentFormValue={selectedTimeValue}
                />
            )}
        </>
    );
};

export default TimePicker;
